import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Upload, Search, Filter, CheckCircle, XCircle, FileSpreadsheet, Loader2, ChevronDown, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import api from '../../lib/api';
import { EXACT_LOGO } from '../../lib/logos';

const ENTITY_TABS = [
  { key: 'tw1_locaties', label: 'TW1 Locaties' },
  { key: 'tw1_group', label: 'TW1 Group BV' },
  { key: 'tw1_holding', label: 'TW1 Holding BV' },
  { key: 'truckshop1', label: 'Truckshop 1' },
  { key: 'techniek_beheer', label: 'Techniek en Beheer' },
  { key: 'truckstop8', label: 'Truckstop 8 BV' },
];

export default function GrootboekTab() {
  const [activeEntity, setActiveEntity] = useState('tw1_locaties');
  const [overview, setOverview] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showBlocked, setShowBlocked] = useState(true);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [entityMeta, setEntityMeta] = useState(null);
  const fileRef = useRef(null);

  const loadOverview = useCallback(async () => {
    try {
      const res = await api.get('/api/gl-accounts');
      setOverview(res.data.entities || []);
    } catch { /* silent */ }
  }, []);

  const loadAccounts = useCallback(async (entityType) => {
    setLoading(true);
    try {
      const res = await api.get(`/api/gl-accounts/${entityType}`);
      setAccounts(res.data.accounts || []);
      setEntityMeta({ updated_at: res.data.updated_at, updated_by: res.data.updated_by });
    } catch {
      setAccounts([]);
      setEntityMeta(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadAccounts(activeEntity); setUploadPreview(null); setSearchTerm(''); setTypeFilter('all'); }, [activeEntity, loadAccounts]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Alleen .xlsx bestanden worden ondersteund');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', activeEntity);
      const res = await api.post('/api/gl-accounts/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadPreview(res.data);
      toast.success(`${res.data.total} rekeningen gevonden`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload mislukt');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!uploadPreview) return;
    setSaving(true);
    try {
      await api.post('/api/gl-accounts/save', {
        entity_type: activeEntity,
        accounts: uploadPreview.accounts,
      });
      toast.success('Rekeningen opgeslagen');
      setUploadPreview(null);
      loadAccounts(activeEntity);
      loadOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Opslaan mislukt');
    } finally { setSaving(false); }
  };

  const entityInfo = overview.find(e => e.entity_type === activeEntity);
  const types = [...new Set(accounts.map(a => a.type).filter(Boolean))].sort();
  const filtered = accounts.filter(a => {
    if (!showBlocked && a.blocked) return false;
    if (typeFilter !== 'all' && a.type !== typeFilter) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return a.code.toLowerCase().includes(s) || a.description.toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div className="space-y-4" data-testid="grootboek-tab">
      {/* Entity sub-tabs */}
      <div className="flex flex-wrap gap-2" data-testid="entity-tabs">
        {ENTITY_TABS.map(tab => {
          const info = overview.find(e => e.entity_type === tab.key);
          return (
            <button
              key={tab.key}
              onClick={() => setActiveEntity(tab.key)}
              data-testid={`entity-tab-${tab.key}`}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                activeEntity === tab.key
                  ? 'bg-[#003366] text-white shadow-md'
                  : 'bg-gray-100 text-[#526071] hover:bg-gray-200'
              }`}
            >
              {tab.label}
              {info && info.total > 0 && (
                <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                  activeEntity === tab.key ? 'bg-white/20' : 'bg-gray-200'
                }`}>{info.total}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Upload + Info bar */}
      <div className="tw1-card p-4" data-testid="grootboek-info">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#003366]/10 flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-[#003366]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#0b1220]">{ENTITY_TABS.find(t => t.key === activeEntity)?.label}</h3>
              {entityInfo && entityInfo.total > 0 ? (
                <p className="text-xs text-[#526071]">
                  {entityInfo.active} actief, {entityInfo.blocked} geblokkeerd
                  {entityMeta?.updated_by && ` — Bijgewerkt door ${entityMeta.updated_by}`}
                  {entityMeta?.updated_at && ` op ${new Date(entityMeta.updated_at).toLocaleDateString('nl-NL')}`}
                </p>
              ) : (
                <p className="text-xs text-[#526071]">Geen rekeningen geladen</p>
              )}
            </div>
          </div>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleUpload}
              className="hidden"
              data-testid="grootboek-file-input"
            />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              variant="outline"
              className="gap-2 border-[#003366]/20 text-[#003366] hover:bg-[#003366]/5"
              data-testid="grootboek-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Importeer uit Exact Online
            </Button>
          </div>
        </div>
      </div>

      {/* Upload Preview */}
      {uploadPreview && (
        <div className="tw1-card p-4 border-2 border-[#f5a800]/40 bg-[#f5a800]/5" data-testid="upload-preview">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-[#f5a800]" />
            <h4 className="font-semibold text-[#0b1220]">Upload preview</h4>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-3 bg-white rounded-lg">
              <div className="text-2xl font-bold text-[#003366]">{uploadPreview.total}</div>
              <div className="text-xs text-[#526071]">Totaal gevonden</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <div className="text-2xl font-bold text-emerald-600">{uploadPreview.active}</div>
              <div className="text-xs text-[#526071]">Actief</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <div className="text-2xl font-bold text-red-500">{uploadPreview.blocked}</div>
              <div className="text-xs text-[#526071]">Geblokkeerd</div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#003366] hover:bg-[#002244] gap-2"
              data-testid="grootboek-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Bevestigen & Opslaan
            </Button>
            <Button variant="outline" onClick={() => setUploadPreview(null)} data-testid="grootboek-cancel-btn">
              Annuleren
            </Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="grootboek-filters">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#526071]" />
          <Input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Zoek op code of omschrijving..."
            className="pl-10"
            data-testid="grootboek-search"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[200px]" data-testid="grootboek-type-filter">
            <Filter className="w-4 h-4 mr-2 text-[#526071]" />
            <SelectValue placeholder="Filter op type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle typen</SelectItem>
            {types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm text-[#526071] cursor-pointer">
          <input
            type="checkbox"
            checked={showBlocked}
            onChange={e => setShowBlocked(e.target.checked)}
            className="rounded"
            data-testid="grootboek-show-blocked"
          />
          Toon geblokkeerd ({accounts.filter(a => a.blocked).length})
        </label>
        <span className="text-xs text-[#526071] ml-auto">
          {filtered.length} van {accounts.length} rekeningen
        </span>
      </div>

      {/* Accounts Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[#003366]" />
        </div>
      ) : (
        <div className="tw1-card overflow-hidden" data-testid="grootboek-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-[#003366] w-24">Code</th>
                  <th className="px-4 py-3 text-left font-semibold text-[#003366]">Omschrijving</th>
                  <th className="px-4 py-3 text-left font-semibold text-[#003366] w-40">Type</th>
                  <th className="px-4 py-3 text-left font-semibold text-[#003366] w-32">Categorie</th>
                  <th className="px-4 py-3 text-left font-semibold text-[#003366] w-24">D/C</th>
                  <th className="px-4 py-3 text-center font-semibold text-[#003366] w-20">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-[#526071]">
                      {accounts.length === 0 ? 'Geen rekeningen geladen voor deze entiteit' : 'Geen resultaten voor deze zoekopdracht'}
                    </td>
                  </tr>
                ) : (
                  filtered.map((a, i) => (
                    <tr
                      key={`${a.code}-${i}`}
                      className={`border-b border-gray-100 transition-colors ${
                        a.blocked
                          ? 'bg-gray-50/50 text-gray-400'
                          : 'hover:bg-[#003366]/[0.02]'
                      }`}
                      data-testid={`account-row-${a.code}`}
                    >
                      <td className={`px-4 py-2.5 font-mono text-xs font-medium ${a.blocked ? 'line-through' : 'text-[#003366]'}`}>
                        {a.code}
                      </td>
                      <td className={`px-4 py-2.5 ${a.blocked ? 'line-through' : ''}`}>
                        {a.description}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          a.type === 'Omzet' ? 'bg-emerald-50 text-emerald-700' :
                          a.type === 'RC-rekening' ? 'bg-blue-50 text-blue-700' :
                          a.type === 'Kostprijs omzet' ? 'bg-amber-50 text-amber-700' :
                          a.type === 'Afschrijvingen' ? 'bg-purple-50 text-purple-700' :
                          a.type === 'Financiële lasten' ? 'bg-red-50 text-red-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {a.type || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-[#526071]">{a.category || '-'}</td>
                      <td className="px-4 py-2.5 text-xs text-[#526071]">{a.debit_credit || '-'}</td>
                      <td className="px-4 py-2.5 text-center">
                        {a.blocked ? (
                          <XCircle className="w-4 h-4 text-red-400 mx-auto" />
                        ) : (
                          <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" />
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
